import { State } from 'ts-fsrs';
import { resolveConfig } from './fsrs.js';
import { studyDayStart, studyDayEnd } from './day.js';
import { descendantIds, liveDecks } from '../db/repo/decks.js';

const LEARN_AHEAD_MS = 20 * 60 * 1000;

export function isActive(card, now) {
  return !card.deletedAt && !card.suspended && !(card.buriedUntil && card.buriedUntil > now);
}

function stateOf(card) {
  return card.fsrs?.state ?? State.New;
}

/**
 * Classify cards into today's queues and apply daily limits.
 * Pure function so it can be unit-tested and reused for deck counts.
 */
export function buildQueue(cards, { now = Date.now(), config } = {}) {
  const c = resolveConfig(config);
  const dayStart = studyDayStart(now);
  const dayEnd = studyDayEnd(now);

  let newToday = 0;
  let reviewsToday = 0;
  const notesSeenToday = new Set();
  for (const card of cards) {
    if (card.deletedAt) continue;
    if (card.lastReviewedAt >= dayStart) {
      notesSeenToday.add(card.noteId);
      if (card.firstReview >= dayStart) newToday++;
      else reviewsToday++;
    }
  }

  const learnNow = [];
  const learnLater = [];
  const reviews = [];
  const news = [];
  for (const card of cards) {
    if (!isActive(card, now)) continue;
    const st = stateOf(card);
    if (st === State.Learning || st === State.Relearning) {
      if (card.due <= now) learnNow.push(card);
      else if (card.due < dayEnd) learnLater.push(card);
    } else if (st === State.Review) {
      if (card.due < dayEnd && !(notesSeenToday.has(card.noteId) && card.lastReviewedAt < dayStart)) {
        reviews.push(card);
      }
    } else if (!notesSeenToday.has(card.noteId)) {
      news.push(card);
    }
  }
  learnNow.sort((a, b) => a.due - b.due);
  learnLater.sort((a, b) => a.due - b.due);
  reviews.sort((a, b) => a.due - b.due);
  news.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0) || a.ord - b.ord);

  const reviewsLeft = Math.max(0, Number(c.reviewsPerDay) - reviewsToday);
  const newLeft = Math.max(0, Number(c.newPerDay) - newToday);
  const limitedReviews = reviews.slice(0, reviewsLeft);
  const limitedNew = news.slice(0, newLeft);

  return {
    learnNow,
    learnLater,
    reviews: limitedReviews,
    news: limitedNew,
    counts: {
      new: limitedNew.length,
      learn: learnNow.length + learnLater.length,
      review: limitedReviews.length,
    },
    done: { newToday, reviewsToday },
    newOrder: c.newOrder,
  };
}

/** Choose the next card. Returns { card } | { waitUntil } | { finished: true }. */
export function pickNext(queue, { now = Date.now(), sessionCount = 0 } = {}) {
  if (queue.learnNow.length) return { card: queue.learnNow[0] };
  const r = queue.reviews.length;
  const n = queue.news.length;
  if (r && n) {
    if (queue.newOrder === 'after') return { card: queue.reviews[0] };
    if (queue.newOrder === 'before') return { card: queue.news[0] };
    const every = Math.max(2, Math.ceil((r + n) / n));
    return { card: sessionCount % every === every - 1 ? queue.news[0] : queue.reviews[0] };
  }
  if (r) return { card: queue.reviews[0] };
  if (n) return { card: queue.news[0] };
  if (queue.learnLater.length) {
    const first = queue.learnLater[0];
    if (first.due - now <= LEARN_AHEAD_MS) return { card: first };
    return { waitUntil: first.due };
  }
  return { finished: true };
}

/** Cards of a deck subtree (or every deck when deckId is null). */
export async function loadDeckCards(db, deckId) {
  const decks = await db.decks.toArray();
  const ids = deckId ? descendantIds(decks, deckId) : liveDecks(decks).map((d) => d.id);
  if (!ids.length) return { cards: [], decks };
  const cards = await db.cards.where('deckId').anyOf(ids).toArray();
  return { cards: cards.filter((c) => !c.deletedAt), decks };
}

/**
 * Today's queue across decks, each root using its own daily limits.
 * Child decks are included with their parent, and a card is only counted once.
 * Pass rootIds to study a subset; omit it to study every top-level deck.
 */
export function buildTodayQueue(decks, cards, { now = Date.now(), rootIds = null } = {}) {
  const live = liveDecks(decks);
  const liveIds = new Set(live.map((d) => d.id));
  const roots = rootIds?.length
    ? rootIds.filter((id) => liveIds.has(id))
    : live.filter((d) => !d.parentId || !liveIds.has(d.parentId)).map((d) => d.id);

  const byDeck = new Map(live.map((d) => [d.id, []]));
  for (const card of cards) {
    if (!card.deletedAt) byDeck.get(card.deckId)?.push(card);
  }

  const learnNow = [];
  const learnLater = [];
  const reviews = [];
  const news = [];
  const seen = new Set();
  const take = (list, into) => {
    for (const card of list) {
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      into.push(card);
    }
  };

  for (const id of roots) {
    const deck = live.find((d) => d.id === id);
    if (!deck) continue;
    const subset = descendantIds(live, id).flatMap((deckId) => byDeck.get(deckId) ?? []);
    const q = buildQueue(subset, { now, config: deck.config });
    take(q.learnNow, learnNow);
    take(q.learnLater, learnLater);
    take(q.reviews, reviews);
    take(q.news, news);
  }

  const byDue = (a, b) => a.due - b.due;
  learnNow.sort(byDue);
  learnLater.sort(byDue);
  reviews.sort(byDue);
  news.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0) || a.ord - b.ord);

  return {
    learnNow,
    learnLater,
    reviews,
    news,
    counts: { new: news.length, learn: learnNow.length + learnLater.length, review: reviews.length },
    newOrder: 'mix',
  };
}

/** Today's counts for every deck (including subdecks), each with its own limits. */
export function computeAllCounts(decks, cards, now = Date.now()) {
  const live = liveDecks(decks);
  const byDeck = new Map(live.map((d) => [d.id, []]));
  for (const card of cards) byDeck.get(card.deckId)?.push(card);
  const result = new Map();
  for (const deck of live) {
    const subtree = descendantIds(live, deck.id);
    const subset = subtree.flatMap((id) => byDeck.get(id) ?? []);
    const q = buildQueue(subset, { now, config: deck.config });
    result.set(deck.id, { ...q.counts, total: subset.length });
  }
  return result;
}
